import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { VIEW_MODE_TOGGLE_KEY_CAPTURE_SELECTOR } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/DocumentViewModeToggle'
import type { ViewMode } from 'features/knowledgeDocumentViewer/types'
import type { SensorSearchItem } from 'features/knowledgeDocumentViewer/types/searchableItems'
import React, { useEffect } from 'react'
import type { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'

export interface UseDocumentResultsKeyboardNavigationParams {
  activeDocument?: DocumentTableRow | null
  documentResults?: DocumentTableRow[]
  enabled: boolean
  filteredDocuments?: DocumentTableRow[]
  handleSensorClick?: (sensor: SensorSearchItem) => void
  handleSensorDoubleClick?: (sensor: SensorSearchItem) => void
  mode: DocumentsViewerContentFilter
  onSearchDocumentNavigate?: (
    doc: DocumentTableRow,
    newTab: boolean,
    isLinkedDocsMode: boolean
  ) => void
  selectedDocumentId?: string | null
  selectedSensor?: SensorSearchItem | null
  sensorResults?: SensorSearchItem[]
  setActiveIndex?: React.Dispatch<React.SetStateAction<number>>
  setSelectedDocumentId?: (id: string) => void
  viewMode?: ViewMode
}

export function useDocumentResultsKeyboardNavigation({
  activeDocument,
  documentResults,
  enabled,
  filteredDocuments,
  handleSensorClick,
  handleSensorDoubleClick,
  mode,
  onSearchDocumentNavigate,
  selectedDocumentId,
  selectedSensor,
  sensorResults,
  setActiveIndex,
  setSelectedDocumentId,
  viewMode,
}: UseDocumentResultsKeyboardNavigationParams): void {
  useEffect(() => {
    const documents = documentResults ?? []
    const filtered = filteredDocuments ?? []
    const sensors = sensorResults ?? []
    const hasDocuments = mode === 'documents' && documents.length > 0
    const hasSensors = mode === 'sensors' && sensors.length > 0
    if (!enabled) return
    if (!hasDocuments && !hasSensors) return

    const handleKeyDown = (event: KeyboardEvent) => {
      const isArrowOrEnter =
        event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        event.key === 'Enter'
      if (!isArrowOrEnter) return

      if (!(event.target instanceof HTMLElement)) return
      const el = event.target
      const isInput =
        el.tagName === 'INPUT' ||
        el.tagName === 'TEXTAREA' ||
        el.tagName === 'SELECT' ||
        el.getAttribute('contenteditable') === 'true'
      const isViewModeToggle = el.closest(VIEW_MODE_TOGGLE_KEY_CAPTURE_SELECTOR)
      if (isInput && !isViewModeToggle) return

      event.preventDefault()

      if (mode === 'sensors') {
        if (!handleSensorClick || !handleSensorDoubleClick) return
        if (event.key === 'Enter') {
          if (selectedSensor) handleSensorDoubleClick(selectedSensor)
          return
        }
        const currentIndex = selectedSensor
          ? sensors.findIndex((sensor) => sensor.id === selectedSensor.id)
          : -1
        const base = currentIndex < 0 ? 0 : currentIndex
        const delta = event.key === 'ArrowDown' ? 1 : -1
        const nextIndex = Math.min(
          sensors.length - 1,
          Math.max(0, base + delta)
        )
        handleSensorClick(sensors[nextIndex])
        return
      }

      if (event.key === 'Enter') {
        if (!activeDocument || !onSearchDocumentNavigate) return
        onSearchDocumentNavigate(
          activeDocument,
          event.metaKey || event.ctrlKey,
          false
        )
        return
      }

      if (viewMode === 'card') {
        if (!setActiveIndex) return
        const delta = event.key === 'ArrowDown' ? 1 : -1
        setActiveIndex((prev) =>
          Math.min(filtered.length - 1, Math.max(0, prev + delta))
        )
      } else {
        if (!setSelectedDocumentId) return
        const currentIndex = selectedDocumentId
          ? documents.findIndex((result) => result.id === selectedDocumentId)
          : -1
        const base = currentIndex < 0 ? 0 : currentIndex
        const delta = event.key === 'ArrowDown' ? 1 : -1
        const nextIndex = Math.min(
          documents.length - 1,
          Math.max(0, base + delta)
        )
        setSelectedDocumentId(documents[nextIndex].id)
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [
    activeDocument,
    documentResults,
    enabled,
    filteredDocuments,
    handleSensorClick,
    handleSensorDoubleClick,
    mode,
    onSearchDocumentNavigate,
    selectedDocumentId,
    selectedSensor,
    sensorResults,
    setActiveIndex,
    setSelectedDocumentId,
    viewMode,
  ])
}
